// src/context/ContextConfigView.jsx
//
// Admin editor for tenant context config files, keyed on an explicit tenantId
// (mirrors DocumentsView). Mounted ONLY in the superadmin console — direct edits of
// these files are not offered to workspace admins. ⚠️ UI-only: the backend still
// accepts PUT /context/singletons/{name} and /context/tenant-profile from a workspace
// admin (`TenantAdmin` gate), so this does not close that door:
//   • superadmin console   → admin/ContextSettingsPage (tenant dropdown, canWrite)
// NO member surface (config is not member-facing).
//
// GETs return verbatim `raw` + `etag`; we edit raw in a plain monospace textarea
// and PUT { raw } so hand-written comments are preserved (no YAML/markdown library
// touches the text). canWrite gates editing.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../constants/theme.js';
import {
  getSingleton, putSingleton, getTenantProfile, putTenantProfile,
} from './contextApi.js';

const FONT = FONT_SANS;
const ACCENT = COLORS.accent;

// Labels are resolved via i18n (context:config.items.<key>) at render time.
const ITEMS = [
  { key: 'website-urls',    kind: 'singleton' },
  { key: 'internal-links',  kind: 'singleton' },
  { key: 'tenant-settings', kind: 'singleton' },
  { key: 'model-config',    kind: 'singleton', optionalOverride: true }, // NEW
  { key: 'tenant-profile',  kind: 'profile'   },
];

// Seeded into the editor by "Crea override" when a tenant has no model-config file.
const MODEL_CONFIG_TEMPLATE =
  '# Override di routing dei modelli per questo tenant.\n' +
  '# Ogni chiave è un agente della pipeline; i valori sovrascrivono i default globali.\n' +
  '# Esempio:\n' +
  '# writer:\n' +
  '#   provider: anthropic\n' +
  '#   model: claude-opus-4-8\n' +
  '#   temperature: 0.7\n' +
  '#   max_tokens: 4096\n';

function loadItem(tenantId, item) {
  return item.kind === 'profile'
    ? getTenantProfile({ tenant: tenantId })
    : getSingleton({ tenant: tenantId, name: item.key });
}
function saveItem(tenantId, item, raw, etag) {
  return item.kind === 'profile'
    ? putTenantProfile({ tenant: tenantId, raw, etag })
    : putSingleton({ tenant: tenantId, name: item.key, raw, etag });
}

export default function ContextConfigView({ tenantId, canWrite = false }) {
  const { t } = useTranslation('context');
  const [activeKey, setActiveKey] = useState(ITEMS[0].key);
  const [draft, setDraft] = useState('');       // textarea contents (raw)
  const [baseline, setBaseline] = useState('');  // last-loaded raw (dirty check)
  const [etag, setEtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState(null);  // 422 details.errors[]
  const [notice, setNotice] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [noOverride, setNoOverride] = useState(false); // model-config GET 404 → no tenant override
  const [creating, setCreating] = useState(false);     // user is seeding a new override (no file yet)

  const dirty = draft !== baseline;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const load = useCallback(async (item) => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setError(''); setDetails(null); setOkMsg(''); setNotice(''); setNoOverride(false); setCreating(false);
    try {
      const data = await loadItem(tenantId, item);
      setDraft(data.raw ?? '');
      setBaseline(data.raw ?? '');
      setEtag(data.etag || '');
    } catch (e) {
      // For an optional-override item (model-config), a 404 just means the tenant has
      // no override and uses global defaults — not an error.
      if (item.optionalOverride && (e.status === 404 || e.code === 'CONTEXT_ENTITY_NOT_FOUND')) {
        setNoOverride(true);
        setDraft(''); setBaseline(''); setEtag('');
      } else {
        setError(e.message);
        setDraft(''); setBaseline(''); setEtag('');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Reload on tenant change or item selection.
  useEffect(() => {
    const item = ITEMS.find(i => i.key === activeKey) || ITEMS[0];
    load(item);
  }, [load, activeKey]);

  function selectKey(next) {
    if (next === activeKey) return;
    if (dirtyRef.current && !window.confirm(t('config.discardConfirm'))) return;
    setNotice('');
    setActiveKey(next);
  }

  function startCreateOverride() {
    setNoOverride(false);
    setCreating(true);
    setBaseline('');                 // keep baseline empty so the draft is "dirty" → Salva enabled
    setDraft(MODEL_CONFIG_TEMPLATE);
    setEtag('');                     // no If-Match on create
    setError(''); setDetails(null); setOkMsg(''); setNotice('');
  }
  function cancelCreateOverride() {
    setCreating(false);
    setNoOverride(true);             // back to "Nessun override configurato"
    setDraft(''); setBaseline(''); setEtag('');
    setError(''); setDetails(null); setOkMsg(''); setNotice('');
  }

  async function handleSave() {
    const item = ITEMS.find(i => i.key === activeKey) || ITEMS[0];
    setSaving(true);
    setError(''); setDetails(null); setNotice(''); setOkMsg('');
    try {
      const res = await saveItem(tenantId, item, draft, etag);
      setEtag(res.etag || '');   // update from response — do NOT re-GET
      setBaseline(draft);
      setOkMsg(res.created ? t('config.created') : t('config.saved'));
      setCreating(false);
    } catch (e) {
      if (e.code === 'CONTEXT_ETAG_MISMATCH') {
        await load(item);        // reload server value + etag; local edits discarded
        setNotice(t('config.etagNotice'));
      } else if (e.code === 'CONTEXT_ENTITY_INVALID') {
        setError(e.message);
        setDetails(e.details?.errors || null);  // keep the draft so the user fixes & resaves
      } else {
        setError(e.message);
      }
    } finally {
      setSaving(false);
    }
  }

  if (!tenantId) {
    return <div style={emptyBox}>{t('noTenant')}</div>;
  }

  return (
    <div>
      {error && <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>{error}</div>}
      {Array.isArray(details) && details.length > 0 && (
        <ul style={{ ...alertBox(SEMANTIC.errore.fg), listStyle: 'disc', paddingLeft: '32px' }}>
          {details.map((d, i) => <li key={i}>{typeof d === 'string' ? d : JSON.stringify(d)}</li>)}
        </ul>
      )}
      {notice && <div style={alertBox(SEMANTIC.attenzione.fg)}>{notice}</div>}
      {okMsg && <div style={alertBox(SEMANTIC.verde.fg)}>{okMsg}</div>}

      {/* Item selector */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {ITEMS.map(it => (
          <button
            key={it.key}
            type="button"
            onClick={() => selectKey(it.key)}
            style={it.key === activeKey ? tabActive : tabIdle}
          >
            {t(`config.items.${it.key}`)}
          </button>
        ))}
      </div>

      {noOverride ? (
        <div style={overrideEmptyBox}>
          <p style={{ margin: '0 0 4px', fontWeight: 600, color: COLORS.ink }}>
            {t('config.noOverride.title')}
          </p>
          <p style={{ margin: '0 0 16px' }}>
            {t('config.noOverride.body')}
          </p>
          {canWrite && (
            <button type="button" onClick={startCreateOverride} style={primaryBtn(false)}>
              {t('config.noOverride.create')}
            </button>
          )}
        </div>
      ) : (
        <>
          <textarea
            value={loading ? '' : draft}
            onChange={e => { setDraft(e.target.value); setOkMsg(''); }}
            readOnly={!canWrite || loading}
            spellCheck={false}
            placeholder={loading ? t('config.loading') : ''}
            style={{
              width: '100%', minHeight: '420px', boxSizing: 'border-box',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '13px', lineHeight: 1.5, padding: '12px',
              borderRadius: RADII.control, border: `1px solid ${COLORS.borderStrong}`,
              background: canWrite ? COLORS.surface : COLORS.field, color: COLORS.ink,
              resize: 'vertical', whiteSpace: 'pre', overflowWrap: 'normal', overflowX: 'auto',
            }}
          />

          {canWrite && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
              <button type="button" disabled={saving || loading || !dirty} onClick={handleSave} style={primaryBtn(saving || loading || !dirty)}>
                {saving ? t('config.saving') : t('common:actions.save')}
              </button>
              <button
                type="button"
                disabled={saving || loading || (!dirty && !creating)}
                onClick={creating
                  ? cancelCreateOverride
                  : () => { setDraft(baseline); setError(''); setDetails(null); setOkMsg(''); }}
                style={ghostBtn(saving || loading || (!dirty && !creating))}
              >
                {creating ? t('config.cancelCreate') : t('config.cancelEdit')}
              </button>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: COLORS.textSecondary }}>
                {dirty ? t('config.dirty') : t('config.clean')}
              </span>
            </div>
          )}
        </>
      )}
      {!noOverride && (
        <p style={{ fontSize: '12px', color: COLORS.textSecondary, margin: '12px 0 0' }}>
          {t('config.footer')}
          {activeKey === 'model-config' && (
            <>
              {' '}
              <strong>{t('config.advancedLabel')}</strong>{t('config.advancedBody')}
            </>
          )}
        </p>
      )}
    </div>
  );
}

// ── inline styles ───────────────────────────────────────────────────────────
const emptyBox = {
  padding: '60px 40px', textAlign: 'center', background: COLORS.surface,
  borderRadius: RADII.control, border: `1px solid ${COLORS.border}`,
  color: COLORS.textSecondary, fontSize: '13px', fontFamily: FONT,
};

const overrideEmptyBox = {
  padding: '24px', border: '1px solid #e5e5e7', borderRadius: RADII.controlInner,
  background: COLORS.field, color: COLORS.textSecondary, fontSize: '14px', fontFamily: FONT,
};

function alertBox(color) {
  return {
    marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner,
    background: `${color}14`, border: `1px solid ${color}33`,
    color, fontSize: '13px', fontFamily: FONT,
  };
}

function primaryBtn(disabled) {
  return {
    padding: '8px 16px', borderRadius: RADII.controlInner, border: 'none',
    background: disabled ? COLORS.borderStrong : ACCENT, color: COLORS.surface,
    fontSize: '13px', fontWeight: 600, fontFamily: FONT,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function ghostBtn(disabled) {
  return {
    padding: '6px 12px', borderRadius: RADII.controlInner,
    border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
    color: COLORS.ink, fontSize: '12px', fontFamily: FONT,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };
}

const tabBase = {
  padding: '6px 14px', borderRadius: RADII.controlInner, fontSize: '13px',
  fontFamily: FONT, cursor: 'pointer', border: `1px solid ${COLORS.borderStrong}`,
};
const tabActive = { ...tabBase, background: COLORS.accentSoft, color: ACCENT, fontWeight: 600, borderColor: 'rgba(232,0,45,0.3)' };
const tabIdle = { ...tabBase, background: COLORS.surface, color: COLORS.ink };
